import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAppName } from '../utils/getAppName';
import { Mountain, ExternalLink, Mail, Lock, Trash2 } from 'lucide-react';

interface AuthSuccessProps {
  redirectUrl?: string;
  isExtension?: boolean;
  signOut?: () => Promise<void>;
  onChangeEmail?: () => void;
  onChangePassword?: () => void;
  onDeleteAccount?: () => void;
}

export const AuthSuccess: React.FC<AuthSuccessProps> = ({ redirectUrl, isExtension, signOut, onChangeEmail, onChangePassword, onDeleteAccount }) => {
  const { user, generateCustomToken } = useAuth();
  const [error, setError] = useState('');

  const handleGenerateToken = useCallback(async () => {
    setError('');
    
    try {
      const token = await generateCustomToken();
      
      // If there's a redirect URL, use fragment identifier for secure token passing
      if (redirectUrl) {
        const url = new URL(redirectUrl);
        // Use hash fragment - this is NOT sent to the server
        url.hash = `token=${token}&t=${Date.now()}`;
        
        // Add a small delay to show "Redirecting..." message
        setTimeout(() => {
          window.location.href = url.toString();
        }, 1000);
      }

      // If it's an extension, send the token to the background script
      if (isExtension) {
        // Assuming you have a way to send messages to the extension background script
        // Add a small delay to show "Redirecting..." message
        setTimeout(() => {
          window.parent.postMessage({ type: 'AUTH_SUCCESS', token }, '*');
        }, 500);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate authentication token');
    }
  }, [generateCustomToken, redirectUrl, isExtension]);

  useEffect(() => {
    // If there's a redirect URL, automatically generate a token
    if ((redirectUrl || isExtension) && user) {
      handleGenerateToken();
    }
  }, [redirectUrl, user, handleGenerateToken, isExtension]);

  const handleSignOut = async () => {
    try {
        if (signOut) {
          await signOut();
        }
    } catch (err: any) {
      setError(err.message || 'Failed to sign out');
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-gradient-to-br from-green-500 to-blue-600 p-3 rounded-xl">
              <Mountain className="h-10 w-10 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Welcome back!
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Signed in as {user.email}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {(redirectUrl || isExtension) && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md text-sm">
              <p className="font-medium">Redirecting to your app...</p>
              <p className="text-xs mt-1">You'll be automatically signed in to {redirectUrl ? getAppName(redirectUrl) : 'your extension'}</p>
            </div>
          )}

          {!(redirectUrl || isExtension) && (
            <>
              <div className="pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Quick Access</h4>
                <div className="space-y-2">
                  <a
                    href="https://weather.roamapps.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-900">Roam Weather</span>
                    <ExternalLink className="h-4 w-4 text-gray-400" />
                  </a>
                  <a
                    href="https://office.roamapps.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-900">Roam Office Tracker</span>
                    <ExternalLink className="h-4 w-4 text-gray-400" />
                  </a>
                </div>
              </div>

              <div className="pt-4 border-t space-y-2">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Account Settings</h4>
                <button
                  onClick={onChangeEmail}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Mail className="h-4 w-4" />
                  Change Email
                </button>
                <button
                  onClick={onChangePassword}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Lock className="h-4 w-4" />
                  Change Password
                </button>
              </div>

              <div className="pt-4 border-t">
                <button
                  onClick={onDeleteAccount}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Account
                </button>
              </div>
            </>
          )}

          <div className="pt-4 border-t">
            <button
              onClick={handleSignOut}
              className="w-full py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
